import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const FundTransferList = () => {
  const [transferData, setTransferData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Date filtering and summary states
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return {
      start: formatDate(firstDay),
      end: formatDate(lastDay)
    };
  };

  const { start: defaultStart, end: defaultEnd } = getCurrentMonthDates();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [uniqueBanks, setUniqueBanks] = useState(0);

  const fetchData = async (filterStartDate = null, filterEndDate = null) => {
    setLoading(true);

    try {
      // Construct query parameters for date filtering
      const params = new URLSearchParams();
      if (filterStartDate) {
        params.append('start_date', filterStartDate);
      }
      if (filterEndDate) {
        params.append('end_date', filterEndDate);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/accounts/fund-transfers?${queryString}` : `/accounts/fund-transfers`;
      
      const res = await Http.get(url);
      const resRole = await Http.get(`/staff-type-check`);
      
      // Helper to detect ISO UTC date strings
      const isIsoUtcDateString = (str) =>
        typeof str === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(str);

      const formatUtcToLocalDisplay = (isoStr) => {
        const d = new Date(isoStr); // parsed as UTC, represented in local time
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
        const day = pad2(d.getDate());
        const monthName = months[d.getMonth()];
        const year = d.getFullYear();
        let hours = d.getHours();
        const minutes = pad2(d.getMinutes());
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        if (hours === 0) hours = 12;
        const hh = pad2(hours);
        return `${day}-${monthName}-${year} ${hh}:${minutes} ${ampm}`;
      };

      // Helper to format numeric amount with thousands separators, preserving decimals exactly
      const formatAmountWithGrouping = (val) => {
        if (val === null || val === undefined) return val;
        const str = String(val);
        if (!/^-?\d+(?:\.\d+)?$/.test(str)) return val; // leave as-is if not a pure number
        const isNeg = str.startsWith('-');
        const raw = isNeg ? str.slice(1) : str;
        const [intPart, decPart] = raw.split('.');
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const result = decPart !== undefined ? `${grouped}.${decPart}` : grouped;
        return isNeg ? `-${result}` : result;
      };

      // Handle backend response structure
      let transfersArray, summaryData;
      
      if (res.data && typeof res.data === 'object' && res.data.data) {
        // New structure with summary data
        transfersArray = res.data.data;
        summaryData = {
          total_amount: res.data.total_amount || 0,
          total_count: res.data.total_count || 0,
          unique_banks: res.data.unique_banks || 0
        };
      } else {
        // Fallback for old structure
        transfersArray = Array.isArray(res.data) ? res.data : [];
        summaryData = {
          total_amount: 0,
          total_count: transfersArray.length,
          unique_banks: 0
        };
      }

      // Transform only top-level date fields and 'amount' on each item; preserve all other fields exactly
      const formattedData = Array.isArray(transfersArray)
        ? transfersArray.map((item) => {
            const formatted = { ...item };
            Object.keys(item).forEach((key) => {
              const val = item[key];
              if (isIsoUtcDateString(val)) {
                formatted[key] = formatUtcToLocalDisplay(val);
              } else if (key === "amount") {
                formatted[key] = formatAmountWithGrouping(val);
              }
            });
            return formatted;
          })
        : transfersArray;

      setTransferData(formattedData);
      setRole(resRole.data);
      
      // Update summary metrics
      setTotalAmount(summaryData.total_amount);
      setTotalRows(summaryData.total_count);
      setUniqueBanks(summaryData.unique_banks);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData(startDate, endDate);
  }, []);

  // Date filter handlers
  const handleDateFilter = () => {
    fetchData(startDate, endDate);
  };

  const clearDateFilter = () => {
    const { start, end } = getCurrentMonthDates();
    setStartDate(start);
    setEndDate(end);
    fetchData(start, end);
  };

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const cols = [
    { field: "transfer_number", header: "Transfer Number" },
    { field: "date", header: "Date" },
    { field: "from_bank.name", header: "From Bank" },
    { field: "to_bank.name", header: "To Bank" },
    { field: "amount", header: "Amount" },
    // { field: "status", header: "Status" }
  ];

  const actionTemplate = (item) => {
    return (
      <td className="text-xs text-center">
        <Link
          to={`/view-fund-transfer/${item.id}`}
          className="btn btn-info btn-sm ms-1"
          title="View Fund Transfer"
        >
          <i className="fa fa-list" aria-hidden="true"></i>
        </Link>
        {(role === "admin" || role === "accounts") && (
          <Link
            to={`/edit-fund-transfer/${item.id}`}
            className="btn btn-primary btn-sm ml-1"
            title="Edit"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Link>
        )}
      </td>
    );
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">All Fund Transfers</h5>
            <div className="am-right">
              {(role === "admin" || role === "accounts") && (
                <Link
                  to="/create-fund-transfer"
                  className="btn btn-primary"
                >
                  <i className="fa fa-plus"></i> Add New Fund Transfer
                </Link>
              )}
            </div>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            {/* Filter Section */}
            <div className="card pd-20 pd-sm-40 mb-4">
              {/* Date Range Indicator */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="alert alert-info border-0 bg-light-info">
                    <i className="fa fa-calendar me-2 text-info"></i>
                    <strong>Data showing from:</strong> {startDate} <strong>to</strong> {endDate}
                  </div>
                </div>
              </div>

              {/* Filter Controls */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fa fa-calendar-o me-1"></i>
                    From Date
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">
                    <i className="fa fa-calendar-o me-1"></i>
                    To Date
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary me-2 px-4"
                    onClick={handleDateFilter}
                  >
                    <i className="fa fa-search me-1"></i>
                    Apply Filter
                  </button>
                  <button
                    className="btn btn-outline-secondary px-4"
                    onClick={clearDateFilter}
                  >
                    <i className="fa fa-refresh me-1"></i>
                    Reset
                  </button>
                </div>
              </div>

              {/* Additional Summary Cards */}
              <div className="row">
                <div className="col-6">
                  <div className="card border-0 bg-light-success">
                    <div className="card-body p-2 text-center">
                      <h6 className="text-success mb-1">
                        <i className="fa fa-list-alt me-1"></i>
                        Total Records
                      </h6>
                      <h5 className="text-success mb-0">{totalRows}</h5>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-0 bg-light-primary">
                    <div className="card-body p-3 text-center">
                      <h6 className="text-primary mb-1">
                        <i className="fa fa-money me-1"></i>
                        Total Amount
                      </h6>
                      <h4 className="text-primary mb-0">
                        ৳{totalAmount ? totalAmount.toLocaleString() : '0'}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card pd-20 pd-sm-40">
              <ReactDataTable
                cols={cols}
                receiveData={transferData}
                isLoading={loading}
                actionTemplate={actionTemplate}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundTransferList;